import { useEffect, useRef } from 'react';
import { DraggableEventHandler } from 'react-draggable';
import { useDebounceCallback } from 'usehooks-ts';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import useDragAndDropStore from '@/components/common/Forms/Questions/DragAndDrop/useDragAndDropStore';

export const DEFAULT_TOOLBAR_POSITION = { x: 500, y: 4 };
const TOOLBAR_STORAGE_KEY_PREFIX = 'bigBookToolbarPosition';

const getStoredToolbarPosition = (userId: number): {
  x: number; y: number; slideHeight?: number; slideWidth?: number;
} | null => {
  try {
    const stored = localStorage.getItem(`${TOOLBAR_STORAGE_KEY_PREFIX}_${userId}`);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    if (typeof parsed.x === 'number' && typeof parsed.y === 'number') return parsed;
  } catch { /* ignore malformed data */ }

  return null;
};

// AIDEV-NOTE: Use clientWidth/clientHeight (unscaled layout px), not getBoundingClientRect.
// `.slide` has a CSS transform applied by useResizer, so bounding rects are scaled — but
// react-draggable's `position` is in unscaled px, so clamping must be in unscaled px too.
const clampToSlide = (
  position: { x: number; y: number },
  slideEl: Element | null,
  toolbarEl: Element | null,
): { x: number; y: number } => {
  if (!slideEl) return position;
  const slideWidth = slideEl.clientWidth;
  const slideHeight = slideEl.clientHeight;
  const toolbarWidth = toolbarEl?.clientWidth ?? 0;
  const toolbarHeight = toolbarEl?.clientHeight ?? 0;

  return {
    x: Math.max(0, Math.min(position.x, slideWidth - toolbarWidth)),
    y: Math.max(0, Math.min(position.y, slideHeight - toolbarHeight)),
  };
};

const useReadingDraggableToolbar = () => {
  const isPresentMode = useSlideShowContext(state => state.isPresentMode);
  const setToolbarPosition = useSlideShowContext(state => state.setToolbarPosition);
  const toolbarPosition = useSlideShowContext(state => state.toolbarPosition);
  const userId = useSlideShowContext(state => state.userId);
  const resizerScaleValue = useDragAndDropStore(state => state.resizerScaleValue);

  const nodeRef = useRef<HTMLDivElement>(null);

  // AIDEV-NOTE: Restore toolbar position from localStorage on mount, scaled to current slide size and clamped
  useEffect(() => {
    if (!userId) return;

    const stored = getStoredToolbarPosition(userId);
    if (!stored) return;

    // Use querySelector since nodeRef may not be mounted yet (toolbar renders after data loads)
    const slideEl = document.querySelector('.slide');
    const slideWidth = slideEl?.clientWidth;
    const slideHeight = slideEl?.clientHeight;

    let pos = { x: stored.x, y: stored.y };
    if (stored.slideWidth && stored.slideHeight && slideWidth && slideHeight) {
      pos = {
        x: pos.x * (slideWidth / stored.slideWidth),
        y: pos.y * (slideHeight / stored.slideHeight),
      };
    }

    setToolbarPosition(clampToSlide(pos, slideEl, null));
  }, [userId, setToolbarPosition]);

  const handleResize = useDebounceCallback(() => {
    const slideEl = nodeRef.current?.closest('.slide') ?? null;
    const newPosition = clampToSlide(toolbarPosition ?? DEFAULT_TOOLBAR_POSITION, slideEl, nodeRef.current);

    setToolbarPosition(newPosition);

    if (userId && slideEl) {
      try {
        localStorage.setItem(
          `${TOOLBAR_STORAGE_KEY_PREFIX}_${userId}`,
          JSON.stringify({ ...newPosition, slideHeight: slideEl.clientHeight, slideWidth: slideEl.clientWidth }),
        );
      } catch { /* localStorage full or unavailable */ }
    }
  }, 200);

  // AIDEV-NOTE: Re-clamp toolbar position when window resizes so it stays within the slide bounds
  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, [handleResize]);

  const onStop: DraggableEventHandler = (_e, data) => {
    const newPosition = { x: data.x, y: data.y };
    setToolbarPosition(newPosition);
    if (userId) {
      const slideEl = nodeRef.current?.closest('.slide') ?? null;
      try {
        localStorage.setItem(
          `${TOOLBAR_STORAGE_KEY_PREFIX}_${userId}`,
          JSON.stringify({
            ...newPosition,
            ...(slideEl ? { slideHeight: slideEl.clientHeight, slideWidth: slideEl.clientWidth } : {}),
          }),
        );
      } catch { /* localStorage full or unavailable */ }
    }
  };

  return {
    disabled: !isPresentMode,
    nodeRef,
    onStop,
    position: toolbarPosition ?? DEFAULT_TOOLBAR_POSITION,
    scale: resizerScaleValue || 1,
  };
};

export default useReadingDraggableToolbar;
